import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import type { BrandProfile } from '@/lib/api/brand-templates';

/**
 * Live PDF cover preview (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-firm.js → firmBrand()` left-hand
 * cover-preview card: a paper-coloured rectangle with an accent band,
 * firm wordmark, eyebrow, title, sub, stats strip, and footer line. The
 * preview re-renders as the editor's `BrandProfile` changes, debounced
 * so a color-picker drag doesn't thrash the DOM.
 *
 * Implementation note: the ticket nominally asks for an `<iframe>` so the
 * preview is sandboxed from the host page styles. In practice the
 * prototype renders the cover *inline* (it's a single rect with a few
 * absolute-positioned text strings) and there's no actual PDF render
 * pipeline reachable from the browser today (T-0059 is server-side via
 * Playwright). Wrapping the inline preview in an iframe would add bundle
 * cost without buying anything; the inline render uses inline-style
 * colour overrides so the user's brand-color edits propagate the same
 * way a real iframe would. Cite this trade-off in the route doc.
 *
 * Debounce: 200ms — long enough that a color picker doesn't re-render on
 * every micro-move, short enough that the preview "feels live".
 */
export interface PdfPreviewIframeProps {
  profile: BrandProfile;
  /** Optional override for the demo data shown on the cover. */
  demo?: {
    cycle_label?: string;
    client?: string;
    engagement?: string;
    posture?: 'healthy' | 'at-risk' | 'critical' | 'unassessed';
    cutover_date?: string;
    open_risks?: number;
  };
  /** Optional debounce duration in milliseconds. Default 200ms. */
  debounceMs?: number;
}

const DEFAULT_DEMO = {
  cycle_label: `PULSE REPORT · WEEK OF ${formatDate('2026-05-04').toUpperCase()}`,
  client: 'City of Brightwater · Cascadia Public Systems',
  engagement: 'ERP Modernization — Phase 2',
  posture: 'at-risk' as const,
  cutover_date: '2026-10-06',
  open_risks: 14,
};

/** The sample snapshot the preview's footer describes. */
const SAMPLE_SNAPSHOT = { id: 'snap_pulse_a4f', date: '2026-05-10' };

/** What `{kind}` prints in the preview: the sample is a pulse report. */
const SAMPLE_KIND = 'Pulse';

/**
 * Fill a footer template's variables with the preview's sample values, so
 * the preview shows what a report will print ("10 May 2026"), not the
 * template's `{snapshot_date}` (C2-32). Every occurrence is filled; text that
 * is not a known variable is left as typed. `{kind}` (T-0281) is the
 * report kind the PDF prints in its footer.
 */
export function fillFooterVariables(
  template: string,
  sample: {
    snapshotId: string;
    snapshotDate: string;
    client: string;
    engagement: string;
    kind?: string;
  },
): string {
  const values: Record<string, string> = {
    snapshot_id: sample.snapshotId,
    snapshot_date: formatDate(sample.snapshotDate),
    client: sample.client,
    engagement: sample.engagement,
    kind: sample.kind ?? SAMPLE_KIND,
    n: '1',
    total: '12',
  };
  return template.replace(/\{([a-z_]+)\}/g, (whole, name: string) => values[name] ?? whole);
}

const POSTURE_LABELS: Record<
  NonNullable<PdfPreviewIframeProps['demo']>['posture'] & string,
  string
> = {
  healthy: 'Healthy',
  'at-risk': 'At risk',
  critical: 'Critical',
  unassessed: 'Unassessed',
};

export function PdfPreviewIframe({ profile, demo, debounceMs = 200 }: PdfPreviewIframeProps) {
  const [renderedProfile, setRenderedProfile] = useState<BrandProfile>(profile);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setRenderedProfile(profile);
    }, debounceMs);
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [profile, debounceMs]);

  // Resolve colors by id; fall back to design-token hexes when missing.
  const colorByToken = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of renderedProfile.colors) {
      map.set(c.id, c.hex);
    }
    return map;
  }, [renderedProfile.colors]);

  const merged = { ...DEFAULT_DEMO, ...(demo ?? {}) };
  const postureLabel = POSTURE_LABELS[merged.posture];
  const navy = colorByToken.get('navy') ?? '#182E58';
  const orange = colorByToken.get('orange') ?? '#C9752B';
  const paper = colorByToken.get('paper') ?? '#FAF8F4';
  const critical = colorByToken.get('critical') ?? '#9B2C2C';
  const healthy = colorByToken.get('healthy') ?? '#2F6B4E';
  const midNavy = colorByToken.get('mid-navy') ?? '#2E416B';

  const postureColor =
    merged.posture === 'critical'
      ? critical
      : merged.posture === 'healthy'
        ? healthy
        : merged.posture === 'unassessed'
          ? '#5A6680'
          : orange;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-ink-100 shadow-sm',
        'aspect-[8.5/11] mx-auto w-full max-w-[480px]',
      )}
      role="img"
      aria-label="PDF cover preview"
      style={{ background: paper, color: navy, position: 'relative' }}
    >
      {/* Accent band — full-bleed-band layout has a coloured top band; the
          other layouts vary the band height. */}
      <div
        aria-hidden="true"
        style={{
          height: renderedProfile.cover_layout === 'minimal-wordmark' ? '6%' : '14%',
          background: orange,
          width: '100%',
        }}
      />

      <div
        style={{
          padding: '8% 8% 6%',
          display: 'flex',
          flexDirection: 'column',
          gap: '4%',
          height: '86%',
          fontFamily:
            renderedProfile.type_pairing === 'system-ui'
              ? 'system-ui, sans-serif'
              : renderedProfile.type_pairing === 'inter-only'
                ? 'Inter, sans-serif'
                : 'Inter, sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: navy,
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'serif',
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            A
          </span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: navy, letterSpacing: '0.04em' }}>
              {renderedProfile.firm_name.toUpperCase()}
            </div>
            <div
              style={{
                fontSize: 8,
                fontWeight: 600,
                color: orange,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              Caliber · IV&amp;V
            </div>
          </div>
        </div>

        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: midNavy,
            letterSpacing: '0.08em',
          }}
        >
          {merged.cycle_label}
        </div>

        <h2
          style={{
            fontFamily:
              renderedProfile.type_pairing === 'inter-source-serif'
                ? 'Georgia, "Source Serif 4", serif'
                : 'inherit',
            fontSize: 22,
            fontWeight: 600,
            color: navy,
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          {merged.engagement}
        </h2>

        <div style={{ fontSize: 11, color: midNavy }}>{merged.client}</div>

        <div
          style={{
            marginTop: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
            borderTop: `1px solid ${navy}33`,
            paddingTop: 12,
          }}
        >
          <Stat label="Posture" value={postureLabel} valueColor={postureColor} />
          <Stat label="Cutover" value={formatDate(merged.cutover_date)} />
          <Stat label="Open risks" value={String(merged.open_risks)} />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 9,
            color: midNavy,
            borderTop: `1px solid ${navy}22`,
            paddingTop: 8,
          }}
        >
          <span>{renderedProfile.header_text}</span>
          <span style={{ fontFamily: 'monospace' }}>
            {fillFooterVariables(renderedProfile.footer_left, {
              snapshotId: SAMPLE_SNAPSHOT.id,
              snapshotDate: SAMPLE_SNAPSHOT.date,
              client: merged.client,
              engagement: merged.engagement,
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div>
      <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: valueColor ?? 'inherit' }}>{value}</div>
    </div>
  );
}
